import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { confirmClientWorkExit, resetClientWorkExit } from '../../utils/clientWorkNavigation'
import { Link, NavLink, useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  BarChart3,
  BellRing,
  Cable,
  CalendarClock,
  CheckSquare,
  Command,
  Database,
  Eye,
  FileInput,
  FilePlus2,
  FileText,
  Image,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  Monitor,
  Palette,
  Plug,
  PlusCircle,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Tags,
  UserCircle,
  Users,
} from 'lucide-react'
import { AppShell, Button, DropdownMenu, ProductNavigation } from '../common'
import { useAuth } from '../../hooks/useAuth'
import { useOrganization } from '../../hooks/useOrganization'
import {
  getCommandDestinations,
  getProductNavigation,
  getVisibleProducts,
  organizationSwitchPath,
  resolveStudioRoute,
  STUDIO_PRODUCT_IDS,
} from '../../config/studioRoutes'
import { staticAssetUrl } from '../../utils/staticAssets'
import OrganizationSwitcher from '../OrganizationSwitcher'
import NotificationCenter from '../NotificationCenter'
import { CommandSearch } from './CommandSearch'
import { ProductSwitcher } from './ProductSwitcher'
import { ThemeToggle } from './ThemeToggle'
import styles from './StudioAppShell.module.css'

const ICONS = {
  BarChart3,
  BellRing,
  Cable,
  CalendarClock,
  CheckSquare,
  Database,
  Eye,
  FileInput,
  FilePlus2,
  FileText,
  Image,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  Monitor,
  PanelsTopLeft: Palette,
  Palette,
  Smartphone,
  PlusCircle,
  Plug,
  Search,
  Settings,
  Settings2: Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Tags,
  ChartNoAxesCombined: BarChart3,
  Users,
}
const DESKTOP_NAVIGATION_STORAGE_KEY = 'wager-studio:desktop-navigation-collapsed'

function readDesktopNavigationPreference() {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(DESKTOP_NAVIGATION_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function writeDesktopNavigationPreference(collapsed) {
  try {
    window.localStorage.setItem(DESKTOP_NAVIGATION_STORAGE_KEY, String(collapsed))
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}


const denyAccess = () => false
const ignoreOrganizationSwitch = () => {}

export function StudioAppShell({ children, commandCenter = false }) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, logout, commandCenterAccess } = useAuth()
  const {
    currentOrg,
    organizations: organizationList,
    switchOrganization: organizationSwitchOrganization,
    canAccessResource: organizationCanAccessResource,
    hasProductAccess: organizationHasProductAccess,
  } = useOrganization()
  const organizations = useMemo(() => organizationList || [], [organizationList])
  const switchOrganization = organizationSwitchOrganization || ignoreOrganizationSwitch
  const canAccessResource = organizationCanAccessResource || denyAccess
  const hasProductAccess = organizationHasProductAccess || denyAccess
  const [mobileOpen, setMobileOpenState] = useState(false)
  // A product picked in the mobile drawer: its landing route keeps the drawer
  // open, once, so the next tap picks one of its pages (#729). Any later
  // navigation closes it, however it happens (NavLink, command palette, an
  // in-page link).
  const mobileProductRef = useRef(null)
  const setMobileOpen = useCallback((open) => {
    if (!open) mobileProductRef.current = null
    setMobileOpenState(open)
  }, [])
  const [navigationCollapsed, setNavigationCollapsed] = useState(readDesktopNavigationPreference)

  const accessContext = useMemo(() => ({
    currentOrg,
    organizations,
    commandCenterAccess,
    canAccessResource,
    hasProductAccess,
  }), [canAccessResource, commandCenterAccess, currentOrg, hasProductAccess, organizations])
  const products = useMemo(() => getVisibleProducts(accessContext), [accessContext])
  const switcherProducts = useMemo(
    () => products.filter((product) => product.id !== STUDIO_PRODUCT_IDS.COMMAND_CENTER),
    [products],
  )
  const resolvedRoute = resolveStudioRoute(location.pathname)
  const activeProduct = commandCenter
    ? STUDIO_PRODUCT_IDS.COMMAND_CENTER
    : resolvedRoute?.product || products[0]?.id || STUDIO_PRODUCT_IDS.PLATFORM
  const activeProductDefinition = products.find((product) => product.id === activeProduct) || products[0]
  // A deep link into a product this user can't see (for example one hidden
  // from organization roles) keeps the fallback product's navigation rather
  // than an empty section under its label.
  const navigationProduct = products.some((product) => product.id === activeProduct)
    ? activeProduct
    : activeProductDefinition?.id || activeProduct
  const navigationItems = getProductNavigation(navigationProduct, accessContext, location.pathname).map((item) => {
    const Icon = ICONS[item.icon]
    return { ...item, href: item.path, icon: Icon ? <Icon size={17} aria-hidden="true" /> : null }
  })
  const destinations = getCommandDestinations(accessContext, location.pathname)
  const canSwitchOrganizations = organizations.length > 1
  const organizationCommands = canSwitchOrganizations ? organizations : []

  useEffect(() => {
    const pickedProduct = mobileProductRef.current
    if (pickedProduct && resolveStudioRoute(location.pathname)?.product === pickedProduct) {
      mobileProductRef.current = null
      return
    }
    setMobileOpen(false)
  }, [location.pathname, setMobileOpen])

  const handleNavigationCollapsedChange = (collapsed) => {
    setNavigationCollapsed(collapsed)
    writeDesktopNavigationPreference(collapsed)
  }

  const handleLogout = async () => {
    if (!confirmClientWorkExit()) return
    try {
      await logout()
    } catch (error) {
      resetClientWorkExit()
      throw error
    }
    // Cached answers belong to the session that fetched them: the next person
    // to sign in on this tab may hold a lesser role in the same organization.
    queryClient.clear()
    navigate('/login')
  }

  const handleOrganizationSelect = async (slug) => {
    if (!slug || slug === currentOrg?.slug) return
    // The destination is checked against the organization the switch
    // refreshed, not the one this tab loaded: its grants may have changed.
    const nextOrganization = await switchOrganization(slug)
    if (!nextOrganization) return
    queryClient.clear()
    navigate(organizationSwitchPath(location.pathname, nextOrganization, { organizations, commandCenterAccess }))
  }

  const handleProductChange = (productId) => {
    const selected = switcherProducts.find((product) => product.id === productId)
    if (selected) navigate(selected.defaultPath)
  }
  const handleMobileProductChange = (productId) => {
    const selected = switcherProducts.find((product) => product.id === productId)
    // Picking the product whose landing page is open changes no route, so
    // there is no landing to keep the drawer open for.
    if (selected && selected.defaultPath !== location.pathname) mobileProductRef.current = productId
    handleProductChange(productId)
  }
  // Command Center is cross-organization: organization scope lives in each
  // page's own filters, so the shell never shows the organization switcher
  // there. The rail keeps its product context slot filled with a fixed
  // "Command Center" block instead of reserving blank space where the
  // product switcher would render.
  const productSwitcher = commandCenter ? (
    <ProductSwitcher
      products={activeProductDefinition ? [activeProductDefinition] : []}
      value={activeProductDefinition?.id || ''}
      compact={navigationCollapsed}
    />
  ) : (
    <ProductSwitcher
      products={switcherProducts}
      value={activeProductDefinition?.id || ''}
      onChange={handleProductChange}
      compact={navigationCollapsed}
    />
  )
  const mobileProductSwitcher = commandCenter ? (
    <ProductSwitcher
      products={activeProductDefinition ? [activeProductDefinition] : []}
      value={activeProductDefinition?.id || ''}
    />
  ) : (
    <ProductSwitcher
      products={switcherProducts}
      value={activeProductDefinition?.id || ''}
      onChange={handleMobileProductChange}
    />
  )
  const commandCenterButton = !commandCenter && commandCenterAccess ? (
    <Button variant="secondary" className={styles.commandCenterButton} onClick={() => navigate('/command-center')}>
      <Command size={17} aria-hidden="true" />
      <span>Command Center</span>
    </Button>
  ) : null
  const commandSearch = (
    <CommandSearch
      destinations={destinations}
      organizations={organizationCommands}
      onNavigate={navigate}
      onOrganizationSelect={handleOrganizationSelect}
      enableShortcut={!mobileOpen}
    />
  )
  const renderTaskLink = (item, content, linkProps) => (
    <NavLink {...linkProps} to={item.path} onClick={() => setMobileOpen(false)}>{content}</NavLink>
  )
  const taskNavigation = (
    <ProductNavigation
      items={navigationItems}
      label="Primary"
      groupLabel={activeProductDefinition?.label || 'Studio'}
      collapsed={navigationCollapsed}
      renderLink={renderTaskLink}
    />
  )
  const mobileTaskNavigation = (
    <ProductNavigation
      items={navigationItems}
      label="Primary"
      groupLabel={activeProductDefinition?.label || 'Studio'}
      renderLink={renderTaskLink}
    />
  )
  const brand = (
    <Link to="/dashboard" className={styles.brandLink} aria-label="Wager Studio">
      <img className={styles.brandCoin} src={staticAssetUrl('site-static/logo-coin.png')} alt="" />
      <span className={styles.brandCopy}>
        <span>Studio</span>
        {commandCenter ? <span>Command Center</span> : null}
      </span>
    </Link>
  )
  // Settings and Users otherwise live only under Product ▸ Platform, which
  // organization admins rarely open; the account menu is where they look.
  const canManageUsers = canAccessResource('users')
  const accountMenuItems = [
    { key: 'settings', label: 'Settings', icon: Settings, onSelect: () => navigate('/dashboard/settings') },
    { key: 'users', label: 'Users', icon: Users, onSelect: () => navigate('/dashboard/users'), hidden: !canManageUsers },
    { key: 'account-separator', separator: true },
    { key: 'logout', label: 'Logout', onSelect: handleLogout, danger: true },
  ]
  const accountMenu = (
    <DropdownMenu
      trigger={(
        <Button variant="ghost" size="compact" className={styles.accountTrigger}>
          <UserCircle size={17} aria-hidden="true" />
          <span>{user?.display_name || user?.email || 'Account'}</span>
        </Button>
      )}
      items={accountMenuItems}
      triggerLabel="Account menu"
    />
  )

  return (
    <AppShell
      brand={brand}
      organizationContext={commandCenter ? null : <OrganizationSwitcher switchable={canSwitchOrganizations} />}
      productSwitcher={productSwitcher}
      navigation={taskNavigation}
      mobileNavigation={mobileTaskNavigation}
      navigationCollapsed={navigationCollapsed}
      onNavigationCollapsedChange={handleNavigationCollapsedChange}
      navigationLabel={`${activeProductDefinition?.label || 'Studio'} navigation`}
      globalActions={<>{commandCenterButton}{commandSearch}<NotificationCenter /><ThemeToggle />{accountMenu}</>}
      mobileNavigationHeader={(
        <div className={styles.mobileContext}>
          {commandCenter ? null : <OrganizationSwitcher switchable={canSwitchOrganizations} id="mobile-organization-switcher" />}
          {commandCenterButton}
          {mobileProductSwitcher}
          <div className={styles.mobileTools}>
            <CommandSearch
              destinations={destinations}
              organizations={organizationCommands}
              onNavigate={navigate}
              onOrganizationSelect={handleOrganizationSelect}
              enableShortcut={false}
            />
            <NotificationCenter />
            <ThemeToggle />
          </div>
        </div>
      )}
      mobileNavigationFooter={(
        <div className={styles.mobileAccount}>
          <span>{user?.display_name || user?.email || 'Account'}</span>
          <Button variant="ghost" onClick={() => navigate('/dashboard/settings')}>Settings</Button>
          {canManageUsers ? <Button variant="ghost" onClick={() => navigate('/dashboard/users')}>Users</Button> : null}
          <Button variant="secondary" onClick={handleLogout}>Logout</Button>
        </div>
      )}
      mobileOpen={mobileOpen}
      onMobileOpenChange={setMobileOpen}
      className={commandCenter ? styles.commandCenterShell : ''}
    >
      {children}
    </AppShell>
  )
}
